import type { GameView, HandView, SeatView } from "@numbet/engine";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Chips, Logo } from "../components/ui.tsx";
import { formatChips, formatDeviation, formatValue, isYearUnit, secondsLeft } from "../format.ts";
import { t } from "../i18n.ts";
import { navigate } from "../router.ts";
import { ActionBar, IdleActions } from "./ActionBar.tsx";
import { Board, Question } from "./Board.tsx";
import { EstimateInput } from "./EstimateInput.tsx";
import { Result } from "./Result.tsx";
import { clock, Seats } from "./Seats.tsx";
import { useServerNow, type RoomConnection } from "./useRoom.ts";

/** „DEIN TIPP 2.800 Meter“ – nach der Auflösung mit Abweichung. */
function OwnTip({ me, hand }: { me: SeatView; hand: HandView }) {
  if (me.estimate === null) return null;
  const { unit, decimals } = hand.question;
  const direction = hand.answer === null ? null : me.estimate === hand.answer ? "exact" : me.estimate > hand.answer ? "high" : "low";
  return (
    <div className="own-estimate">
      <span className="label">
        <span className={hand.phase === "estimate" ? "lock" : undefined} aria-hidden="true">
          🔒
        </span>{" "}
        {t.yourTip}
      </span>
      <span className="value">
        <span className="num">{formatValue(me.estimate, unit, decimals)}</span>
        {!isYearUnit(unit) && <span className="unit">{unit}</span>}
      </span>
      {me.deviation !== null && direction !== null && (
        <span className={`deviation${direction === "exact" ? " exact" : ""}`}>
          {t.deviation(formatDeviation(me.deviation, unit, decimals), direction)}
        </span>
      )}
    </div>
  );
}

function Timer({ deadline, now }: { deadline: number | null; now: number }) {
  if (deadline === null) return null;
  const seconds = secondsLeft(deadline, now);
  return <span className={`timer-text${seconds <= 10 ? " low" : ""}`}>{clock(deadline, now)}</span>;
}

function Dock({ view, hand, me, now, conn }: { view: GameView; hand: HandView; me: SeatView | undefined; now: number; conn: RoomConnection }) {
  const toAct = hand.toActId ? view.players.find((p) => p.id === hand.toActId) : undefined;
  const potTotal = hand.pots.reduce((sum, pot) => sum + pot.amount, 0);
  const myTurn = me !== undefined && hand.toActId === me.id && view.legalActions !== null;
  const recent = conn.log
    .filter((line) => !line.highlight)
    .slice(-3)
    .map((line) => line.text)
    .join(" · ");

  let status = null;
  let actions = null;
  if (me?.inHand && hand.phase === "estimate") {
    const fraction = hand.deadline === null ? 0 : (hand.deadline - now) / view.estimateTimeMs;
    status = (
      <div className={`progress-bar${fraction < 0.17 ? " low" : ""}`} style={{ gridColumn: "1 / -1" }}>
        <span style={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%` }} />
      </div>
    );
    actions = me.hasEstimate ? (
      <div className="turn-info">
        <span className="locked-note">{t.estimateLocked}</span>
        <span className="recent">{t.waitingForEstimates}</span>
        <Timer deadline={hand.deadline} now={now} />
      </div>
    ) : (
      <EstimateInput key={hand.number} unit={hand.question.unit} decimals={hand.question.decimals} send={conn.send} />
    );
  } else if (me?.inHand && hand.phase === "betting") {
    const legal = view.legalActions;
    actions = (
      <>
        <div className="turn-info" aria-live="polite">
          <span>
            {myTurn && legal
              ? legal.callAmount > 0
                ? t.yourTurnCall(formatChips(legal.callAmount))
                : t.yourTurnCheck
              : toAct
                ? `${t.playerTurn(toAct.name)}.`
                : ""}
          </span>
          {recent && <span className="recent desktop-only">{recent}</span>}
          <Timer deadline={hand.deadline} now={now} />
        </div>
        {myTurn && legal ? (
          <ActionBar key={`${hand.number}-${hand.street}-${hand.currentBet}`} legal={legal} hand={hand} potTotal={potTotal} myRoundBet={me.roundBet} send={conn.send} />
        ) : (
          <IdleActions />
        )}
      </>
    );
  } else if (me) {
    actions = (
      <div className="turn-info">
        <span>{me.eliminated ? t.eliminated : me.sittingOut ? t.youSitOut : !me.inHand ? t.waitingForPlayers : ""}</span>
        {recent && <span className="recent">{recent}</span>}
        <Chips amount={me.chips} />
      </div>
    );
  }

  return (
    <DockFrame>
      {status}
      {me?.inHand && me.estimate !== null && (
        <div className="dock-panel">
          <OwnTip me={me} hand={hand} />
          {recent && <span className="dock-recent">{recent}</span>}
        </div>
      )}
      <div className="dock-actions">{actions}</div>
    </DockFrame>
  );
}

/** Feste Leiste unten; ihre Höhe wird gemessen, damit sie nie Inhalte verdeckt. */
function DockFrame({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty("--dock-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--dock-h");
    };
  }, []);
  return (
    <div className="dock" ref={ref}>
      <div className="dock-inner">{children}</div>
    </div>
  );
}

function Menu({ me, conn }: { me: SeatView | undefined; conn: RoomConnection }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const close = () => ref.current?.removeAttribute("open");
  return (
    <details className="menu" ref={ref} style={{ position: "relative" }}>
      <summary className="btn btn-outline btn-round" style={{ listStyle: "none" }}>
        ☰ <span className="visually-hidden">{t.menu}</span>
      </summary>
      <div className="panel stack" style={{ position: "absolute", right: 0, top: "115%", zIndex: 30, width: 240, padding: 12, gap: 8, background: "var(--panel-solid)" }}>
        <a className="btn btn-small btn-outline" href="/regeln" target="_blank" rel="noreferrer" onClick={close}>
          {t.howItWorks}
        </a>
        {me && !me.eliminated && (
          <button
            type="button"
            className="btn btn-small btn-outline"
            onClick={() => {
              conn.send({ type: me.sittingOut ? "sitIn" : "sitOut" });
              close();
            }}
          >
            {me.sittingOut ? t.sitIn : t.sitOut}
          </button>
        )}
        <button
          type="button"
          className="btn btn-small btn-danger"
          onClick={() => {
            conn.send({ type: "leave" });
            navigate("/");
          }}
        >
          {t.leaveRoom}
        </button>
      </div>
    </details>
  );
}

function TurnPill({ view, myId, now }: { view: GameView; myId: string; now: number }) {
  const hand = view.hand;
  if (!hand || hand.phase !== "betting" || !hand.toActId) return null;
  const time = clock(hand.deadline, now);
  const toAct = view.players.find((p) => p.id === hand.toActId);
  return (
    <span className="pill pill-yellow pill-upper turn-pill">
      ▶ {hand.toActId === myId ? t.yourTurnPill(time) : t.playerTurnPill(toAct?.name ?? "?", time)}
    </span>
  );
}

export function Table({ code, conn, myId }: { code: string; conn: RoomConnection; myId: string }) {
  const state = conn.state;
  const now = useServerNow(conn.clockOffset);
  const connected = useMemo(() => new Set(state?.room.connected ?? []), [state?.room.connected]);
  const logEnd = useRef<HTMLLIElement>(null);

  useEffect(() => {
    logEnd.current?.scrollIntoView({ block: "nearest" });
  }, [conn.log.length]);

  if (!state) return null;
  const view = state.view;
  const hand = view.hand;
  const me = view.players.find((p) => p.id === myId);

  return (
    <>
      <header className="topbar topbar-game">
        <Logo />
        <div className="topbar-info">
          {hand && <span className="pill pill-pink pill-upper">{t.questionPill(hand.number, view.handsPerGame)}</span>}
          <span>
            {t.blinds(view.smallBlind, view.bigBlind)} · {code}
          </span>
        </div>
        <Menu me={me} conn={conn} />
      </header>
      <main className="page">
        <div className="table-layout">
          <div className="table-main">
            {view.waitingForPlayers && <div className="banner">{t.waitingForPlayers}</div>}
            {me?.sittingOut && !me.eliminated && (
              <div className="banner row" style={{ justifyContent: "space-between" }}>
                <span>{t.youSitOut}</span>
                <button type="button" className="btn btn-small btn-primary" onClick={() => conn.send({ type: "sitIn" })}>
                  {t.sitIn}
                </button>
              </div>
            )}
            <Seats view={view} myId={myId} connected={connected} now={now} />
            <TurnPill view={view} myId={myId} now={now} />
            {hand && <Question hand={hand} />}
            {hand && <Board hand={hand} />}
            {hand?.phase === "complete" && <Result view={view} hand={hand} myId={myId} now={now} send={conn.send} />}
          </div>
          <aside className="log" aria-label={t.log}>
            <div className="label">{hand ? t.logTitle(hand.number) : t.log}</div>
            <ol>
              {conn.log.map((line) => (
                <li key={line.id} className={line.highlight ? "hl" : ""}>
                  {line.text}
                </li>
              ))}
              <li ref={logEnd} aria-hidden="true" />
            </ol>
          </aside>
        </div>
        <div className="visually-hidden" aria-live="polite">
          {conn.log.at(-1)?.text}
        </div>
      </main>
      {hand && <Dock view={view} hand={hand} me={me} now={now} conn={conn} />}
    </>
  );
}
